/**
 * Env-driven RCRT SDK client with graceful degradation.
 *
 * The site MUST build and run with ZERO env vars (static marketing preview).
 * Two live modes:
 *
 *   1. PROXY MODE (production) — VITE_RCRT_PROXY_URL points at the rcrt-site
 *      server's /rcrt-api endpoint. NO key ships in the bundle; the server
 *      injects Authorization + X-Tenant-ID at runtime and strips the SDK's
 *      browser-EventSource token query params.
 *
 *   2. DIRECT MODE (local dev) — VITE_RCRT_API + VITE_RCRT_API_KEY (tk_*)
 *      + VITE_RCRT_TENANT talk straight to the gateway. Never ship this
 *      mode publicly with a key you care about.
 *
 * Truth note: the SDK README quickstart uses a bare `extractCard(bc)` which
 * is a doc wart — the real API is `CardsModule.extractCard(bc)`.
 */
import { RcrtClient, staticTokenProvider } from '@possibl/rcrt-sdk';

interface RcrtEnv {
  proxyUrl?: string;
  apiUrl?: string;
  apiKey?: string;
  agent?: string;
  tenant?: string;
}

function readEnv(): RcrtEnv {
  // Vite injects import.meta.env.VITE_* at build time. Access defensively so a
  // missing var never throws (the page must remain a static fallback).
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
  return {
    proxyUrl: env.VITE_RCRT_PROXY_URL?.trim() || undefined,
    apiUrl: env.VITE_RCRT_API?.trim() || undefined,
    apiKey: env.VITE_RCRT_API_KEY?.trim() || undefined,
    agent: env.VITE_RCRT_AGENT?.trim() || undefined,
    tenant: env.VITE_RCRT_TENANT?.trim() || undefined,
  };
}

/** Live when a proxy URL is set, or when a direct api url + tk_ key are set. */
export function isRcrtConfigured(): boolean {
  const { proxyUrl, apiUrl, apiKey } = readEnv();
  if (proxyUrl) return true;
  return Boolean(apiUrl && apiKey && apiKey.startsWith('tk_'));
}

/** The resolved base URL the SDK talks to, if configured. */
export function rcrtApiUrl(): string | undefined {
  const { proxyUrl, apiUrl } = readEnv();
  return proxyUrl || apiUrl;
}

/** Default target agent for the Ask-rcrt dock; 'chat' unless overridden. */
export function defaultAgent(): string {
  return readEnv().agent || 'chat';
}

let cached: RcrtClient | null = null;

/**
 * A shared RcrtClient, or null when not configured. One instance per page
 * session — the SDK is designed for one client per workspace session.
 */
export function getRcrtClient(): RcrtClient | null {
  if (!isRcrtConfigured()) return null;
  if (cached) return cached;

  const { proxyUrl, apiUrl, apiKey, tenant } = readEnv();

  if (proxyUrl) {
    // Relative proxy paths ("/rcrt-api") resolve against the current origin.
    const base = proxyUrl.startsWith('/') ? window.location.origin + proxyUrl : proxyUrl;
    // The placeholder token satisfies the SDK's TokenProvider contract; the
    // proxy replaces the Authorization header (and tenant) server-side.
    cached = new RcrtClient({
      apiUrl: base.replace(/\/$/, ''),
      tokenProvider: staticTokenProvider('proxy-managed'),
    });
    return cached;
  }

  cached = new RcrtClient({
    apiUrl: apiUrl!,
    tokenProvider: staticTokenProvider(apiKey!),
  });
  // Workspace API keys require the X-Tenant-ID header on every call.
  if (tenant) cached.setTenantId(tenant);
  return cached;
}
